import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineOfficeBuilding, HiOutlineDownload } from "react-icons/hi";
import { Button } from "@components";

export interface ProjectNumberSummaryBannerProps {
  projectNumberSearch: string;
  count: number;
  total: number;
  isExporting: boolean;
  onExport: () => void;
}

/** Summary banner shown when filtering invoices by a project number. */
export const ProjectNumberSummaryBanner: FC<ProjectNumberSummaryBannerProps> = ({
  projectNumberSearch,
  count,
  total,
  isExporting,
  onExport,
}) => {
  const { t } = useTranslation(["common", "invoice"]);

  return (
    <div
      className={`mt-3 px-4 py-3 rounded-lg border ${
        count > 0
          ? "bg-brand-subtle dark:bg-brand-muted/20 border-brand-muted dark:border-lgb-blue-800"
          : "bg-surface-muted/50 border-border"
      }`}
    >
      {count > 0 ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 flex-1">
            <HiOutlineOfficeBuilding className="h-5 w-5 text-lgb-blue-600 dark:text-brand shrink-0" />
            <div className="flex flex-col flex-1">
              <span className="text-sm font-medium text-secondary">
                {t("invoice:project_total_label")}:{" "}
                <span className="font-semibold">{projectNumberSearch}</span>
              </span>
              <span className="text-xs text-secondary dark:text-muted">
                {count}{" "}
                {count === 1
                  ? t("invoice:invoice_singular")
                  : t("invoice:invoices_plural")}
              </span>
            </div>
            <span className="text-lg font-bold text-lgb-blue-600 dark:text-brand whitespace-nowrap">
              {total.toLocaleString()} NOK
            </span>
          </div>
          <Button
            variant="success"
            onClick={onExport}
            disabled={isExporting}
            icon={<HiOutlineDownload className="h-5 w-5" />}
            title={t("invoice:export_to_excel")}
            aria-label={t("invoice:export_to_excel")}
            aria-busy={isExporting}
          >
            <span>
              {isExporting
                ? t("common:exporting")
                : t("invoice:export_excel")}
            </span>
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <HiOutlineOfficeBuilding className="h-5 w-5 text-muted" />
          <span className="text-sm text-muted">
            {t("invoice:no_invoices_for_project")}:{" "}
            <span className="font-semibold">{projectNumberSearch}</span>
          </span>
        </div>
      )}
    </div>
  );
};
